import { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';

import { FBoxContainer, FHSkeletonLoader, FSearch } from '../../../base';
import { FORTA_WHITE } from '../../../../utils/colors';
import QAStatusCard from './QAStatusCard';
import { Themes } from '../../../../constants';

import type { ChangeEvent } from 'react';
import type { IProvider, ISessionReviewSummary } from '../../../../utils/types';

interface IQAWeeklyOverviewProps {
  loading: boolean;
  reviewSummary: ISessionReviewSummary[];
  onUpdate: (selectedBT: IProvider | null) => void;
}

const QAWeeklyOverview = ({ loading, reviewSummary, onUpdate }: IQAWeeklyOverviewProps) => {
  const [selectBTIndex, setSelectedBTIndex] = useState<number | undefined>();
  const [filteredReviewSummary, setFilteredReviewSummary] =
    useState<typeof reviewSummary>(reviewSummary);

  const handleSelectBT = (index: number, selectedBt: IProvider | null) => {
    setSelectedBTIndex(index);
    onUpdate(selectedBt);
  };

  useEffect(() => {
    setFilteredReviewSummary(reviewSummary);
  }, [reviewSummary]);

  const handleUpdateFieldValues = (searchStr: string) => {
    setFilteredReviewSummary(
      searchStr.length > 0
        ? reviewSummary.filter(
            (reviewSummary) =>
              reviewSummary.provider.first_name
                .toLocaleLowerCase()
                .includes(searchStr.toLowerCase()) ||
              reviewSummary.provider.last_name
                .toLocaleLowerCase()
                .includes(searchStr.toLocaleLowerCase()),
          )
        : reviewSummary,
    );
  };

  return (
    <FBoxContainer
      colorTheme={Themes.Primary}
      title="Weekly overview"
      sx={{ mt: 3, height: '100%', overflow: 'hidden', width: '353px', boxShadow: 'none' }}
      headerStyle={{ flexGrow: 'unset', boxShadow: 'none', borderRadius: '4px 4px 0 0' }}
      contentStyle={{ height: '100%', p: 2, pb: 6 }}
      dataTestId="weeklyOverviewSection"
    >
      <Typography
        variant="body1"
        sx={{
          display: 'flex',
          justifyContent: 'start',
          fontWeight: 700,
          mb: 2,
        }}
      >
        Select Behavior Technicians
      </Typography>
      <FSearch
        data-testid="btSearchInput"
        sx={{
          borderRadius: '8px',
          mr: 3.5,
          mb: 2,
          backgroundColor: 'white',
          boxShadow: 'none',
        }}
        autoComplete="off"
        inputHeight={40}
        placeholder="Behavior technician"
        onChange={(e: ChangeEvent<HTMLInputElement>) => handleUpdateFieldValues(e.target.value)}
        disabled={reviewSummary.length < 1 || loading}
      />
      <Box
        sx={{
          overflowY: 'auto',
          height: '100%',
          pt: 1,
          pr: 2,
          '::-webkit-scrollbar-track': {
            background: FORTA_WHITE,
          },
        }}
      >
        {loading ? (
          <FHSkeletonLoader rowCount={10} height={60} sx={{ mb: 2 }} />
        ) : (
          filteredReviewSummary?.map((value, index) => (
            <Box
              data-testid={`${(value?.provider?.first_name ?? '').replace(
                /\s/g,
                '',
              )}_${(value?.provider?.last_name ?? '').replace(/\s/g, '')}_qaCard`}
              key={value?.provider.id}
              sx={{ mb: 2, cursor: 'pointer', position: 'relative' }}
              onClick={() => {
                if (loading) return;
                handleSelectBT(
                  selectBTIndex !== index ? index : -1,
                  selectBTIndex !== index ? value?.provider : null,
                );
              }}
            >
              <QAStatusCard
                btName={`${value?.provider?.first_name} ${value?.provider?.last_name}`}
                selected={index === selectBTIndex}
                reviewPendingDurationInDays={value?.review_pending_duration_in_days}
                numFailedReviews={value?.num_failed_reviews}
              />
            </Box>
          ))
        )}
      </Box>
    </FBoxContainer>
  );
};

export default QAWeeklyOverview;
